"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import MedicalDashboardSection from "@/components/molecules/medical-section/medical-dashboard/medical-dashboard-section";
import {
  MedicalEdit,
  OrganizerEdit,
  QuoteDecline,
  TravellerEdit,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import useRequestMedical from "@/hooks/api/medical/use-medical-response";
import useFetchQuoteData from "@/hooks/api/quote/use-fetch-quote";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  setQuestionData,
  setQuote,
  updateQuote,
} from "@/store/slices/quote/quote.slice";
import { ApiErrorProps } from "@/types/ApiErrorType";
import { storageAvailable } from "@/utils/common.utils";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { MedicalDashboartTContentype } from "@/utils/schema/medical-dashboard-schema.util";

import Styles from "./medical-page.module.css";
import Style from "./medicalDashboard.module.css";

type MedicalDashboardProps = {
  medicalDashboardContent: MedicalDashboartTContentype;
};

function MedicalDashboard({
  medicalDashboardContent,
}: Readonly<MedicalDashboardProps>) {
  const {
    title,
    supportingText,
    infoBoxContent,
    ctaButtonText,
    mediclConditionText,
    addBtnText,
    editBtnText,
    finishEditingBtnText,
    noMedicalInfoBoxContent,
    noMedicalTitle,
  } = medicalDashboardContent;
  const dispatch = useDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useWizardTranslation();

  const { showLoading, hideLoading } = useLoading();

  const { referenceId, version } = useSelector(
    (state: RootState) => state.quote
  );
  const responsePatched = useRef(false);

  const [veriskResponseParams, setVeriskResponseParams] = useState<{
    travellerId: string | null;
    hashCode: string | null;
    medicalVersion: number | null;
  } | null>(null);

  const searchParams = useSearchParams();
  const { data, loading, error } = useFetchQuoteData(referenceId);
  const editPage = searchParams.get(MedicalEdit);
  const travelerEdit = searchParams.get(TravellerEdit);
  const organizerEdit = searchParams.get(OrganizerEdit);
  const {
    patchMedicalResponse,
    data: medicalResponseData,
    error: medicalResponseError,
    loading: medicalResponseLoading,
  } = useRequestMedical();

  useEffect(() => {
    if (
      (organizerEdit || travelerEdit || editPage) &&
      pathname.includes(`/${t("medical_dashboard_path", "medical-dashboard")}`)
      // pathname.includes("/medical-dashboard")
    ) {
      // To ensure instance availability of isRouteFromMedicalDashBoard in sessionStorage
      sessionStorage.setItem("isRouteFromMedicalDashBoard", "true");
    }
  }, [organizerEdit, travelerEdit, editPage, pathname]);

  const validateHashCode = (hash: string | null) => {
    const REGEX_HASH_CODE = "^[a-zA-Z0-9]+$";
    const regex = new RegExp(REGEX_HASH_CODE);
    return hash && regex.test(hash);
  };

  const handleInvalidData = () => {
    // Handle invalid data

    window.open("/not-found", "_self");
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && !responsePatched.current) {
        if (data) {
          dispatch(setQuote(data));
        }
        const TravellerId = searchParams.get("travellerId");
        const HashCode = searchParams.get("HashCode");
        const medicalVersion = searchParams.get("version");

        // Check for unexpected parameters using some
        const hasUnexpectedParams = Array.from(searchParams.keys()).some(
          (key) =>
            ![
              "travellerId",
              "HashCode",
              "version",
              MedicalEdit,
              TravellerEdit,
              OrganizerEdit,
            ].includes(key)
        );

        if (searchParams.keys() && hasUnexpectedParams) {
          handleInvalidData(); // Handle the unexpected parameter
          return; // Exit early if there's an unexpected parameter
        }

        // Validate travellerId and hashCode
        if (searchParams.keys() && HashCode && !validateHashCode(HashCode)) {
          handleInvalidData();
          return; // Exit early if validation fails
        }

        setVeriskResponseParams({
          travellerId: TravellerId,
          hashCode: HashCode,
          medicalVersion: parseInt(medicalVersion ?? "", 10) || null,
        });

        const veriskObj = sessionStorage.getItem("veriskObj");
        if (veriskObj && searchParams.get("travellerId")) {
          const praseObj = JSON.parse(veriskObj);
          praseObj.hookFlash = true;
          const hookFlashMedical = JSON.stringify(praseObj);

          if (storageAvailable()) {
            sessionStorage.setItem("veriskObj", hookFlashMedical);
          }
        }
      }
      if (!loading && error !== null) {
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          dispatch(setApiError(await apiErrorMappings(error)));
        }
      }
    };
    handleEffect();
  }, [loading, data, searchParams, dispatch, error, router]);
  // Handle errors
  useEffect(() => {
    const handleEffect = async () => {
      if (error || medicalResponseError) {
        // If we receive a 400 this means the user has been denied cover
        if (
          error?.response?.status === 400 ||
          medicalResponseError?.response?.status === 400
        ) {
          let path = `/${t("unable_to_quote_path", QuoteDecline)}`;
          router.replace(path);
          return;
        }

        // If we cant retreive users quote information, handle this first, then verisk sync second
        const errorDetails: ApiErrorProps = error
          ? {
              ...(await apiErrorMappings(error)),
              multiButton: true,
            }
          : {
              ...(await apiErrorMappings(medicalResponseError!)),
              modalDescription:
                "There's a problem with adding your medical conditions right now. Please try again and if the problem persists contact us.",
              multiButton: true,
            };

        dispatch(setApiError(errorDetails));
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, medicalResponseError, dispatch, router]);

  useEffect(() => {
    if (medicalResponseLoading) showLoading();
    if (
      veriskResponseParams?.travellerId &&
      veriskResponseParams?.hashCode &&
      veriskResponseParams?.medicalVersion
    ) {
      if (!responsePatched.current) {
        responsePatched.current = true;
        const patch = async () => {
          await patchMedicalResponse({
            travellerId: veriskResponseParams.travellerId,
            hashCode: veriskResponseParams.hashCode,
            medicalVersion: veriskResponseParams.medicalVersion,
            version,
            viewPage: Routes.MedicalDashboard,
            isAlter: null,
          });
        };
        patch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veriskResponseParams, pathname, router, version, patchMedicalResponse]);

  useEffect(() => {
    if (medicalResponseLoading) showLoading();
    if (!medicalResponseLoading && !loading) hideLoading();
    if (!medicalResponseLoading && medicalResponseData) {
      dispatch(updateQuote(medicalResponseData));
      dispatch(setQuestionData(medicalResponseData));

      // Remove url parameters sent by verisk to avoid recalling api on browser refresh
      let path = pathname;
      if (editPage || travelerEdit || organizerEdit) {
        const queryParams = editPage
          ? MedicalEdit
          : travelerEdit
          ? TravellerEdit
          : OrganizerEdit;
        path = `${pathname}/?${queryParams}=true`;
      }
      router.replace(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalResponseData, medicalResponseLoading, dispatch, loading]);

  // If we receive a 400 this means the user has been denied cover
  if (
    error?.response?.status === 400 ||
    medicalResponseError?.response?.status === 400
  ) {
    // Dont write any dom elements to screen, useEffect will redirect user
    return null;
  }

  // Error retrieving quote data
  if (error) {
    return <div data-testid="medical-dashboard-err">Error...</div>;
  }

  const renderMedicalContentSection = () => {
    return (
      <MedicalDashboardSection
        dashBordTitle={title}
        supportingText={supportingText}
        infoBoxContent={infoBoxContent}
        ctaButtonText={ctaButtonText}
        mediclConditionText={mediclConditionText}
        addBtnText={addBtnText}
        editBtnText={editBtnText}
        finishEditingBtnText={finishEditingBtnText}
        noMedicalTitle={noMedicalTitle}
        noMedicalInfoBoxContent={noMedicalInfoBoxContent}
      />
    );
  };

  return (
    <div
      data-testid="medical-dashboard-main"
      className={`${Styles.question_layout_wrapper} col-span-12`}
    >
      <div
        className={` ${Style.content_box} rounded-b-2xl flex flex-col items-center md:pb-12`}
      >
        {renderMedicalContentSection()}
      </div>
    </div>
  );
}

export default MedicalDashboard;
